import { OpportunityCustomer } from '@/api/b2b';
import { Button } from '@/components/ui/elements/button';
import { PaginatedResource } from '@/utils/api';
import { customerName, followupUrgency } from '@/features/b2b/logic';
import { UseQueryResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { OpportunityColumn } from './OpportunityColumn';
import { TypeBadge } from './TypeBadge';
import { UrgencyPill } from './UrgencyPill';

type SnoozeMutation = {
  mutate: (id: number) => void;
  isPending: boolean;
};

type CustomerOpportunityColumnProps = {
  useData: (page: number) => UseQueryResult<PaginatedResource<OpportunityCustomer>>;
  useSnooze: () => SnoozeMutation;
  title: string;
  subtitle: string;
  emptyLabel: string;
  testid: string;
  linkBase: string;
};

/**
 * Shared shell for the two customer-worklist columns (Past Customer
 * Outreach, Prospects) — both render `OpportunityCustomer` rows and differ
 * only in data source, copy, and the "Open" link's base path.
 */
export const CustomerOpportunityColumn = ({
  useData,
  useSnooze,
  title,
  subtitle,
  emptyLabel,
  testid,
  linkBase,
}: CustomerOpportunityColumnProps) => {
  const [page, setPage] = useState(1);
  const { data, isPending } = useData(page);
  const snooze = useSnooze();

  const customers = data?.results ?? [];

  return (
    <OpportunityColumn
      testid={testid}
      title={title}
      subtitle={subtitle}
      total={data?.paginator.total ?? 0}
      isLoading={isPending}
      page={page}
      lastPage={data?.paginator.lastPage ?? 1}
      onPageChange={setPage}
      emptyLabel={emptyLabel}
    >
      {customers.map((customer) => {
        const urgency = followupUrgency(customer.followup_at);
        return (
          <div key={customer.customer_id} className="rounded border p-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="font-medium">{customerName(customer)}</span>
                <TypeBadge label={customer.organization_type} />
              </div>
              <UrgencyPill label={urgency.label} tone={urgency.tone} />
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">{customer.phone}</div>
            <div className="mt-2 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={snooze.isPending}
                onClick={() => snooze.mutate(customer.customer_id)}
              >
                Snooze
              </Button>
              <Button asChild size="sm">
                <Link to={`${linkBase}/${customer.customer_id}`}>Open</Link>
              </Button>
            </div>
          </div>
        );
      })}
    </OpportunityColumn>
  );
};
